import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Divider } from 'primeng/divider';
import { Message } from 'primeng/message';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';

import { DrawingFile } from '../../../../models/drawing-file.model';
import { QuoteRevisionItem } from '../../../../models/quote-revision-item.model';
import { DrawingFileService } from '../../../services/drawing-file.service';
import { DrawingExtractionInfoComponent } from '../../../shared/drawing-extraction-info/drawing-extraction-info.component';
import { PdfViewerComponent } from '../../../shared/pdf-viewer/pdf-viewer.component';
import { QuoteRevisionItemCapexInformationComponent } from './capex-information.component';
import { QuoteRevisionItemDetailsPanelComponent } from './item-details.component';
import { QuoteRevisionItemPricingInformationComponent } from './pricing-information.component';
import { QuoteRevisionItemConsumablesInformationComponent } from './consumables-information.component';

export type QuoteRevisionItemDrawingPanel =
  | 'details'
  | 'drawing'
  | 'properties'
  | 'dfm'
  | 'pricing'
  | 'capex'
  | 'consumables';

@Component({
  selector: 'app-quote-revision-item-drawing-information',
  imports: [
    QuoteRevisionItemCapexInformationComponent,
    QuoteRevisionItemConsumablesInformationComponent,
    Divider,
    DrawingExtractionInfoComponent,
    QuoteRevisionItemDetailsPanelComponent,
    Message,
    PdfViewerComponent,
    QuoteRevisionItemPricingInformationComponent,
    ProgressSpinner,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
  ],
  templateUrl: './drawing-information.component.html',
})
export class QuoteRevisionItemDrawingInformationComponent
  implements OnChanges, OnDestroy
{
  @Input() item!: QuoteRevisionItem;
  @Input() visible = false;
  @Input({ required: true }) currencyCode!: string;
  @Input() activePanel: QuoteRevisionItemDrawingPanel = 'details';
  @Input() readOnly = false;
  @Input() tradeStudyToolFeaturesEnabled = false;
  @Input() consumablesEnabled = false;

  get showConsumablesTab(): boolean {
    return this.consumablesEnabled && (this.item.children?.length ?? 0) === 0;
  }

  @Output() relinkCadRequested = new EventEmitter<void>();
  @Output() capexSaved = new EventEmitter<QuoteRevisionItem>();

  drawingFile: DrawingFile | null = null;
  drawingLoading = false;
  drawingError: string | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly drawingFileService = inject(DrawingFileService);
  private drawingLoadRequestId = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] || changes['visible']) {
      this.loadDrawingFileIfNeeded();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get linkedDrawing(): { id: number; filename: string } | null {
    return (
      this.item?.drawing_files?.[0] ??
      this.item?.inherited_drawing_files?.[0] ??
      null
    );
  }

  get drawingPricingGeometry(): { id: string; name: string } | null {
    const drawing = this.linkedDrawing;
    if (!drawing) {
      return null;
    }
    return {
      id: `drawing-${drawing.id}`,
      name: drawing.filename || `Drawing PDF ${drawing.id}`,
    };
  }

  get drawingFileUrl(): string | null {
    return this.drawingFile?.file_url ?? null;
  }

  private loadDrawingFileIfNeeded(): void {
    this.drawingLoadRequestId += 1;
    const requestId = this.drawingLoadRequestId;

    this.drawingFile = null;
    this.drawingError = null;
    this.drawingLoading = false;

    const drawingId = this.linkedDrawing?.id;
    if (!this.visible || drawingId == null) {
      return;
    }

    this.drawingLoading = true;
    this.drawingFileService
      .getDrawingFile(drawingId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (drawingFile) => {
          if (requestId !== this.drawingLoadRequestId) {
            return;
          }
          this.drawingLoading = false;
          this.drawingFile = drawingFile;
        },
        error: () => {
          if (requestId !== this.drawingLoadRequestId) {
            return;
          }
          this.drawingLoading = false;
          this.drawingError = 'Unable to load drawing preview.';
        },
      });
  }
}
